import data from '@emoji-mart/data';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material';
import { init } from 'emoji-mart';
import { useState } from 'react';
import { Doc } from 'src/@types/docs';
import { PopoverTrigger } from 'src/components/PopoverTrigger';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { CoverPickerPopOver } from '../../shared/CoverPickerPopOver';
import { CoverReposition } from '../../shared/CoverPickerPopOver/CoverReposition';
import { useDocCoverDeleteMutation } from '../../shared/useDocCoverDeleteMutation';
import { useUpdateDocMutation } from '../../shared/useUpdateDocMutation';

init({ data });

type Props = {
  doc: Doc;
};

interface CoverContainerProps {
  haveCover?: boolean;
}

const CoverContainer = styled('div')<CoverContainerProps>`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  width: 100%;
  min-height: ${(prop) => (prop.haveCover ? '240px' : '56px')};
  height: ${(prop) => (prop.haveCover ? '240px' : '56px')};
  -webkit-box-pack: justify;
  justify-content: space-between;
  position: relative;
`;

const CoverGroup = styled('div')`
  width: 100%;
  height: 240px;
  min-height: 240px;

  .cover-buttons {
    visibility: hidden !important;
  }

  .change-button {
    height: 32px;
  }

  &:hover {
    .cover-buttons {
      visibility: visible !important;
    }
  }

  @media (max-width: 768px) {
    .cover-buttons {
      visibility: visible !important;
    }

    @media print {
      .cover-buttons {
        display: none !important;
      }
    }
  }
`;

const ButtonContainer = styled('div')<CoverContainerProps>`
  display: flex;
  gap: 8px;
  margin-top: 4px;
  padding: 2px;
  z-index: 1;
  position: absolute;
  ${(prop) => (prop.haveCover ? 'bottom' : 'top')}: 8px;
  ${(prop) => (prop.haveCover ? 'right' : 'left')}: 8px;
`;

const ActionButton = styled(LoadingButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  opacity: 0.8,
  color: theme.palette.text.secondary,
  ':hover': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    boxShadow: 'none',
  },
}));

declare global {
  // biome-ignore lint/style/noNamespace: This is a global declaration
  namespace JSX {
    interface IntrinsicElements {
      ['em-emoji']: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        shortcodes?: string;
      };
    }
  }
}

export function DocCover({ doc }: Props) {
  const { updateDoc, isUpdateDocLoading } = useUpdateDocMutation();
  const { isRemovingDocCoverLoading, deleteDocCover } =
    useDocCoverDeleteMutation();
  const { translate } = useLocales();
  const [topOffset, setTopOffset] = useState(doc.cover?.topOffset || 0);
  const [isRepositioning, setIsRepositioning] = useState(false);

  if (!doc.cover) return null;

  const handleDeleteDocCover = async () => {
    await deleteDocCover(doc._id);
  };

  const isImage = doc.cover.type === 'custom' && doc.cover.value;

  const haveCover = true;

  return (
    <CoverContainer haveCover={haveCover}>
      <CoverGroup
        style={{
          background:
            doc.cover.type === 'color' ? doc.cover.value : doc.cover.value,
        }}
      >
        {isImage && (
          <CoverReposition
            topOffset={topOffset}
            setTopOffset={setTopOffset}
            enabled={isRepositioning}
          >
            <img
              src={doc.cover.value}
              alt="cover"
              style={{
                position: 'absolute',
                minHeight: '100%',
                width: '100% !important',
              }}
            />
          </CoverReposition>
        )}
        <ButtonContainer haveCover={haveCover} className="cover-buttons">
          <PopoverTrigger popover={CoverPickerPopOver} context={{ doc }}>
            <ActionButton variant="contained" size="small">
              {translate('collaborate.docs.cover.change_cover')}
            </ActionButton>
          </PopoverTrigger>

          {haveCover && (
            <ActionButton
              variant="contained"
              size="small"
              loading={isRemovingDocCoverLoading}
              onClick={handleDeleteDocCover}
            >
              <Iconify icon="ph:trash" />
            </ActionButton>
          )}
          {!isRepositioning && isImage && (
            <ActionButton
              variant="contained"
              size="small"
              onClick={() => setIsRepositioning(true)}
            >
              {translate('collaborate.docs.cover.reposition')}
            </ActionButton>
          )}

          {isRepositioning && (
            <>
              <ActionButton
                variant="contained"
                size="small"
                onClick={async () => {
                  await updateDoc({
                    _id: doc._id,
                    cover: {
                      ...(doc.cover as any),
                      topOffset,
                    },
                  });
                  setIsRepositioning(false);
                }}
                loading={isUpdateDocLoading}
                startIcon={<Iconify icon="tabler:check" />}
              >
                {translate('shared.buttons.save')}
              </ActionButton>
              <ActionButton
                variant="contained"
                size="small"
                onClick={() => {
                  setIsRepositioning(false);
                  setTopOffset(doc.cover?.topOffset || 0);
                }}
              >
                {translate('shared.buttons.cancel')}
              </ActionButton>
            </>
          )}
        </ButtonContainer>
      </CoverGroup>
    </CoverContainer>
  );
}
